import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Channel,
  ConsentRecordResponse,
  CreateContactRequest,
  RecordConsentRequest,
  ResubscribeRequest,
  UpdateContactRequest,
} from '@outreach/contracts';
import { api, type ContactListParams } from '@/lib/api';

const KEY = 'contacts';

export function useContacts(params: ContactListParams) {
  return useQuery({
    queryKey: [KEY, params],
    queryFn: () => api.contacts.list(params),
    placeholderData: (prev) => prev,
  });
}

export function useConsentHistory(contactId: string | null) {
  return useQuery({
    queryKey: [KEY, contactId, 'consent'],
    queryFn: () => api.contacts.consentHistory(contactId as string),
    enabled: Boolean(contactId),
  });
}

/** Invalidate list + the affected contact after a mutation. */
function useInvalidate() {
  const qc = useQueryClient();
  return (contactId?: string) => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    if (contactId) void qc.invalidateQueries({ queryKey: [KEY, contactId] });
  };
}

export function useCreateContact() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateContactRequest) => api.contacts.create(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateContact(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: UpdateContactRequest) => api.contacts.update(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteContact() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: 'archive' | 'erase' }) =>
      api.contacts.remove(id, mode),
    onSuccess: (_r, v) => invalidate(v.id),
  });
}

export function useBulkDeleteContacts() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      target,
      mode,
    }: {
      target: { ids: string[] } | { all: true; q?: string };
      mode: 'archive' | 'erase';
    }) => api.contacts.bulkDelete(target, mode),
    onSuccess: () => invalidate(),
  });
}

export function useRecordConsent(id: string) {
  const invalidate = useInvalidate();
  return useMutation<ConsentRecordResponse, unknown, RecordConsentRequest>({
    mutationFn: (body) => api.contacts.recordConsent(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useResubscribe(id: string) {
  const invalidate = useInvalidate();
  return useMutation<ConsentRecordResponse, unknown, ResubscribeRequest>({
    mutationFn: (body) => api.contacts.resubscribe(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useWithdraw(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (channel: Channel) => api.contacts.withdraw(id, channel),
    onSuccess: () => invalidate(id),
  });
}
